import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Chip,
  Alert,
  useTheme,
  useMediaQuery,
  Skeleton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  School,
  Schedule,
  Assignment,
  Payment,
  Person,
  Today,
  Quiz,
  CalendarToday,
  LocationOn,
} from '@mui/icons-material';
import moment from 'moment';
import 'moment-timezone';
import { studentAPI } from '../../services/api';
import OverviewStatCard from './components/OverviewStatCard';
import TodaysClassesCard from './components/TodaysClassesCard';
import TeacherMiniCard from './components/TeacherMiniCard';
import JoinClassDialog from './components/JoinClassDialog';
import RescheduleClassDialog from './components/RescheduleClassDialog';
import StudentPanel from './components/StudentPanel';
import {
  PageContainer,
  StatsGrid,
  ContentGrid,
} from '../../components/common';

const StudentDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedClass, setSelectedClass] = useState(null);
  const [joinDialog, setJoinDialog] = useState(false);
  const [rescheduleDialog, setRescheduleDialog] = useState(false);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleLoading, setRescheduleLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);


  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await studentAPI.getDashboard();
      
    
      setDashboardData(response.data);
      setLoading(false);
    } catch (error) {


      setError('Failed to load dashboard data: ' + (error.response?.data?.message || error.message));
      setLoading(false);
    }
  };

  const handleJoinClass = async (classItem) => {
    try {
      const response = await studentAPI.joinClass(classItem._id);
      setSelectedClass(response.data.class);
      setJoinDialog(true);
    } catch (error) {
      setError(error.response?.data?.message || 'Cannot join class at this time');
    }
  };

  const handleRescheduleRequest = (classItem) => {
    setSelectedClass(classItem);
    setRescheduleDialog(true);
  };

  const submitRescheduleRequest = async () => {
    if (!rescheduleReason.trim()) {
      setError('Please provide a reason for rescheduling');
      return;
    }

    try {
      setRescheduleLoading(true);
      await studentAPI.createRescheduleRequest({
        classId: selectedClass._id || selectedClass.id,
        reason: rescheduleReason,
        classDetails: {
          subject: selectedClass.subject,
          teacherName: selectedClass.teacherId?.name || selectedClass.teacher?.name || selectedClass.teacher,
          scheduledDate: selectedClass.scheduledDate || selectedClass.date,
          duration: selectedClass.duration
        }
      });
      
      setRescheduleDialog(false);
      setRescheduleReason('');
      setSelectedClass(null);
      setError('');
      alert('Reschedule request submitted successfully! Admin will contact you soon.');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to submit reschedule request');
    } finally {
      setRescheduleLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'primary';
      case 'ongoing': return 'warning';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const canJoinClass = (scheduledDate) => {
    const classTime = moment.utc(scheduledDate);
    const now = moment();
    const timeDiff = Math.abs(now.diff(classTime, 'minutes'));
    return timeDiff <= 15; // Can join 15 minutes before or after
  };

  const canRequestReschedule = (scheduledDate) => {
    const classTime = moment.utc(scheduledDate);
    const now = moment();
    const hoursDiff = classTime.diff(now, 'hours');
    return hoursDiff > 6; // Can reschedule only if class is more than 6 hours away
  };

  const formatClassTime = (date, classTimezone = 'Asia/Dubai', startTime = null, studentTimezone = null) => {
 

    // Get student's timezone (fallback to class timezone if not provided)
    const targetTimezone = studentTimezone || classTimezone;

    // Parse the date in class timezone first
    const classDate = moment.tz(date, classTimezone);

    // If startTime is provided, create a full datetime in class timezone
    let classDateTime;
    if (startTime) {
      classDateTime = moment.tz(
        `${classDate.format('YYYY-MM-DD')} ${startTime}`,
        'YYYY-MM-DD HH:mm',
        classTimezone
      );
    } else {
      classDateTime = classDate;
    }

    // Convert to student's timezone
    const studentDateTime = classDateTime.clone().tz(targetTimezone);

    // Calculate day labels based on student's timezone
    const now = moment().tz(targetTimezone);
    const tomorrow = moment().tz(targetTimezone).add(1, 'day');
    const dayAfterTomorrow = moment().tz(targetTimezone).add(2, 'days');

    let dayLabel = '';
    if (studentDateTime.isSame(now, 'day')) {
      dayLabel = 'Today';
    } else if (studentDateTime.isSame(tomorrow, 'day')) {
      dayLabel = 'Tomorrow';
    } else if (studentDateTime.isSame(dayAfterTomorrow, 'day')) {
      dayLabel = 'Day After Tomorrow';
    } else {
      dayLabel = studentDateTime.format('dddd'); // Day of week
    }

    const result = {
      dayLabel,
      date: studentDateTime.format('MMM DD'),
      time: studentDateTime.format('h:mm A'),
      fullDate: studentDateTime.format('MMMM DD, YYYY'),
      dayOfWeek: studentDateTime.format('dddd')
    };


    return result;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  };

  // Collapse per-subject assignments into one row per teacher, joining their
  // subjects (mockup shows "Hindi · English") and deriving display initials.
  const prepareAssignedTeachers = (assignments = []) => {
    const byTeacher = new Map();
    assignments.forEach((assignment) => {
      const name = assignment.teacherId?.name || 'Unknown Teacher';
      const existing = byTeacher.get(name);
      if (existing) {
        if (assignment.subject) existing.subjects.push(assignment.subject);
      } else {
        byTeacher.set(name, {
          name,
          initials: getInitials(name),
          subjects: assignment.subject ? [assignment.subject] : [],
        });
      }
    });
    return Array.from(byTeacher.values()).map((teacher) => ({
      name: teacher.name,
      initials: teacher.initials,
      subjectsLabel: teacher.subjects.join(' · '),
    }));
  };

  const LoadingSkeleton = () => (
    <PageContainer>
      <Box sx={{ mb: 4 }}>
        <Skeleton variant="text" width={400} height={48} />
        <Skeleton variant="text" width={250} height={24} sx={{ mt: 1 }} />
      </Box>
      
      <StatsGrid>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
        ))}
      </StatsGrid>
      
      <ContentGrid>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </ContentGrid>
    </PageContainer>
  );

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <PageContainer>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </PageContainer>
    );
  }

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Typography
          component="h1"
          sx={{
            fontWeight: 700,
            fontSize: isMobile ? '21px' : '24px',
            letterSpacing: '-0.01em',
            lineHeight: 1.2,
            color: 'text.primary',
            textAlign: { xs: 'center', sm: 'left' },
          }}
        >
          Welcome back, {
            dashboardData?.student?.name ||
            dashboardData?.user?.name ||
            dashboardData?.name ||
            (() => {
              try {
                const studentUser = localStorage.getItem('student_user');
                if (studentUser) {
                  const userObj = JSON.parse(studentUser);
                  return userObj.name || userObj.firstName || 'Student';
                }
              } catch (e) {
                console.error('Error getting name from localStorage:', e);
              }
              return 'Student';
            })()
          }
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
            mt: '5px',
            justifyContent: { xs: 'center', sm: 'flex-start' },
          }}
        >
          <Typography
            component="span"
            sx={{ color: 'text.secondary', fontSize: '14px', fontWeight: 500 }}
          >
            {dashboardData?.student?.class || 'Grade 10'} · {dashboardData?.student?.subjects?.join(', ') || 'Mathematics, Physics, Chemistry'}
          </Typography>
          <Chip
            icon={<LocationOn />}
            label={dashboardData?.student?.timezone || 'Asia/Dubai'}
            size="small"
            sx={{
              height: 'auto',
              py: '5px',
              borderRadius: '20px',
              fontWeight: 600,
              fontSize: '11.5px',
              color: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.13),
              '& .MuiChip-icon': { color: 'primary.main', fontSize: '14px' },
              '& .MuiChip-label': { px: '8px' },
            }}
          />
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ mb: '28px' }}>
        <StatsGrid
          columns={{ xs: 1, sm: 2, md: 3, lg: 3 }}
          spacing={2}
          sx={{
            '& .MuiGrid-item': {
              display: 'flex',
              '& > *': {
                width: '100%'
              }
            }
          }}
        >
          {[
            {
              title: "Today's Classes",
              value: dashboardData?.todaysClasses?.length || 0,
              icon: <Today />,
              color: 'primary',
              subtitle: `${dashboardData?.todaysClasses?.length || 0} ${(dashboardData?.todaysClasses?.length || 0) === 1 ? 'class' : 'classes'} scheduled today`,
              onClick: () => window.location.href = '/student/classes',
            },
            {
              title: 'Monthly Classes',
              value: dashboardData?.stats?.monthlyClasses || 0,
              icon: <Schedule />,
              color: 'info',
              subtitle: 'Classes this month',
              onClick: () => window.location.href = '/student/classes',
            },
            {
              title: 'Your Teachers',
              value: dashboardData?.stats?.totalTeachers || 0,
              icon: <School />,
              color: 'success',
              subtitle: `${dashboardData?.stats?.totalTeachers || 0} assigned ${(dashboardData?.stats?.totalTeachers || 0) === 1 ? 'teacher' : 'teachers'}`,
              onClick: () => window.location.href = '/student/teachers',
            },
            {
              title: 'Check Homework',
              value: dashboardData?.stats?.pendingHomework ?? 0,
              icon: <Assignment />,
              color: 'warning',
              subtitle: 'Pending Assignments',
              onClick: () => window.location.href = '/student/homework',
            },
            {
              title: 'Quick Tests',
              value: dashboardData?.stats?.availableTests ?? 0,
              icon: <Quiz />,
              color: 'error',
              subtitle: 'Available tests to take',
              onClick: () => window.location.href = '/student/quick-test',
            },
            {
              title: 'View Invoices',
              value: dashboardData?.stats?.totalInvoices ?? 0,
              icon: <Payment />,
              color: 'secondary',
              subtitle: 'Payment records',
              onClick: () => window.location.href = '/student/invoices',
            },
          ].map((card) => (
            <OverviewStatCard key={card.title} {...card} />
          ))}
        </StatsGrid>
      </Box>

      {/* Main Content */}
      <Box sx={{ mb: '28px' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1.6fr 1fr' },
            gap: '18px',
            alignItems: 'start',
          }}
        >
          {/* Today's Classes - Simplified */}
          <StudentPanel
            title="Today's schedule"
            icon={<Today />}
            action={
              <Chip
                icon={<LocationOn />}
                label={`Times in: ${dashboardData?.student?.timezone || 'Asia/Dubai'}`}
                size="small"
                sx={{
                  height: 'auto',
                  py: '5px',
                  borderRadius: '20px',
                  fontWeight: 600,
                  fontSize: '11.5px',
                  color: 'primary.main',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.13),
                  '& .MuiChip-icon': { color: 'primary.main', fontSize: '14px' },
                  '& .MuiChip-label': { px: '8px' },
                }}
              />
            }
            sx={{ height: 'fit-content' }}
          >
          {dashboardData?.todaysClasses?.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {dashboardData.todaysClasses.map((classItem, index) => {
                const studentTimezone = dashboardData?.student?.timezone || 'Asia/Dubai';
                const timeInfo = formatClassTime(classItem.scheduledDate || classItem.date, classItem.timezone, classItem.startTime, studentTimezone);
                return (
                  <TodaysClassesCard
                    key={index}
                    subject={classItem.subject}
                    teacherName={classItem.teacherId?.name || classItem.teacher?.name || classItem.teacher}
                    statusLabel={classItem.status || 'Scheduled'}
                    statusColor={getStatusColor(classItem.status || 'scheduled')}
                    time={timeInfo.time}
                    duration={classItem.duration || 60}
                    room={classItem.room || 'Online'}
                  />
                );
              })}
            </Box>
          ) : (
            <Box
              sx={{
                py: '54px',
                px: '20px',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Box
                sx={{
                  width: 66,
                  height: 66,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  mb: '8px',
                  color: 'primary.main',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.13),
                  '& svg': { fontSize: 26 },
                }}
              >
                <Today />
              </Box>
              <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'text.primary' }}>
                No classes today
              </Typography>
              <Typography sx={{ fontSize: '13px', color: 'text.secondary', maxWidth: 330 }}>
                Enjoy your free time!
              </Typography>
            </Box>
          )}
        </StudentPanel>

        {/* Sidebar Content */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Your Teachers - Simplified */}
          <StudentPanel
            title="Your teacher"
            icon={<School />}
            sx={{ height: 'fit-content' }}
          >
            {(dashboardData?.student?.assignedTeachers?.length > 0) ? (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {prepareAssignedTeachers(dashboardData.student.assignedTeachers).map((teacher, index) => (
                  <TeacherMiniCard
                    key={index}
                    name={teacher.name}
                    initials={teacher.initials}
                    subjectsLabel={teacher.subjectsLabel}
                  />
                ))}
                <Box sx={{ pt: '14px', pb: '6px' }}>
                  <Button
                    disableElevation
                    onClick={() => window.location.href = '/student/teachers'}
                    sx={{
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      px: '12px',
                      py: '7px',
                      color: 'primary.main',
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.13),
                      '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2) },
                    }}
                  >
                    View details
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  py: '54px',
                  px: '20px',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Box
                  sx={{
                    width: 66,
                    height: 66,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    mb: '8px',
                    color: 'primary.main',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.13),
                    '& svg': { fontSize: 26 },
                  }}
                >
                  <Person />
                </Box>
                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'text.primary' }}>
                  No teachers assigned yet
                </Typography>
              </Box>
            )}
          </StudentPanel>
        </Box>
        </Box>
      </Box>

      {/* Upcoming Classes - Clean & Modern */}
      <Box sx={{ mb: '28px' }}>
        <Typography
          component="h2"
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            color: 'text.primary',
            textAlign: { xs: 'center', sm: 'left' },
          }}
        >
          Upcoming Classes
        </Typography>

        {/* Extra spacing after heading */}
        <Box sx={{ height: '14px' }} />

        <StudentPanel>
          {(dashboardData?.upcomingClasses?.length > 0) ? (
            dashboardData.upcomingClasses.slice(0, 3).map((classItem, index) => {
            const studentTimezone = dashboardData?.student?.timezone || 'Asia/Dubai';
            const timeInfo = formatClassTime(classItem.scheduledDate || classItem.date, classItem.timezone, classItem.startTime, studentTimezone);
            const canJoin = canJoinClass(classItem.scheduledDate || classItem.date);
            const canReschedule = canRequestReschedule(classItem.scheduledDate || classItem.date);
            return (
              <TodaysClassesCard
                key={`class-${index}`}
                subject={classItem.subject}
                teacherName={classItem.teacherId?.name || classItem.teacher?.name || classItem.teacher}
                statusLabel={classItem.status || 'Scheduled'}
                statusColor={getStatusColor(classItem.status || 'scheduled')}
                dateLabel={`${timeInfo.dayLabel} · ${timeInfo.date}`}
                time={timeInfo.time}
                duration={classItem.duration || 60}
                room={classItem.room || 'Online'}
                canJoin={canJoin}
                canReschedule={canReschedule}
                onJoin={() => handleJoinClass(classItem)}
                onReschedule={() => handleRescheduleRequest(classItem)}
              />
            );
          })
          ) : (
            <Box
              sx={{
                py: '54px',
                px: '20px',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Box
                sx={{
                  width: 66,
                  height: 66,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  mb: '8px',
                  color: 'primary.main',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.13),
                  '& svg': { fontSize: 26 },
                }}
              >
                <CalendarToday />
              </Box>
              <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'text.primary' }}>
                No upcoming classes
              </Typography>
              <Typography sx={{ fontSize: '13px', color: 'text.secondary', maxWidth: 330 }}>
                Your schedule is clear for the next 7 days
              </Typography>
            </Box>
          )}
        </StudentPanel>
      </Box>

      {/* Join Class Dialog */}
      {selectedClass && (
        <JoinClassDialog
          open={joinDialog}
          onClose={() => setJoinDialog(false)}
          subject={selectedClass.subject}
          teacherName={selectedClass.teacher}
          timeLabel={moment(selectedClass.scheduledDate).format('YYYY-MM-DD HH:mm')}
          duration={selectedClass.duration}
          meetingLink={selectedClass.meetingLink}
          onJoinMeeting={() => window.open(selectedClass.meetingLink, '_blank')}
        />
      )}

      {/* Reschedule Request Dialog */}
      {selectedClass && (
        <RescheduleClassDialog
          open={rescheduleDialog}
          onCancel={() => {
            setRescheduleDialog(false);
            setRescheduleReason('');
            setSelectedClass(null);
          }}
          subject={selectedClass.subject}
          teacherName={selectedClass.teacherId?.name || selectedClass.teacher?.name || selectedClass.teacher}
          dateLabel={moment(selectedClass.scheduledDate || selectedClass.date).format('MMMM DD, YYYY')}
          timeLabel={moment(selectedClass.scheduledDate || selectedClass.date).format('hh:mm A')}
          duration={selectedClass.duration || 60}
          reason={rescheduleReason}
          onReasonChange={setRescheduleReason}
          loading={rescheduleLoading}
          submitDisabled={rescheduleLoading || !rescheduleReason.trim()}
          onSubmit={submitRescheduleRequest}
        />
      )}
    </Box>
  );
};

export default StudentDashboard;